import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  getVideoById,
  getVideoRoute,
  isShortVideo,
  type Video as ServiceVideo,
} from '../../lib/services/videoService';
import { BASE_URL, getNextApiKey } from '../../lib/youtube';
import { useCurrentUser } from '../../contexts/UserContext';
import VideoCard from './VideoCard';

interface Video extends ServiceVideo {
  progress: number;
  lastPosition: number;
  totalWatched: number;
}

interface WatchHistoryEntry {
  progress: number;
  watchedAt: { seconds: number };
  lastPosition: number;
  totalWatched: number;
}

interface WatchHistoryData {
  entries: { [videoId: string]: WatchHistoryEntry };
}

function parseISODuration(iso: string): number {
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  const h = +m[1] || 0, mm = +m[2] || 0, s = +m[3] || 0;
  return h * 3600 + mm * 60 + s;
}

function ContinueWatching() {
  const { t } = useTranslation('home');
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    const loadContinueWatching = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const ref = doc(db, 'userWatchHistory', currentUser.uid);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setVideos([]);
          return;
        }

        const data = snap.data() as WatchHistoryData;
        const entries = data.entries || {};
        const unwatchedVideos = Object.entries(entries)
          .filter(([_, entry]) => entry.progress < 0.95)
          .sort((a, b) => (b[1].watchedAt?.seconds ?? 0) - (a[1].watchedAt?.seconds ?? 0))
          .slice(0, 20);

        const videosWithDetails = await Promise.all(
          unwatchedVideos.map(async ([videoId, entry]) => {
            let video = await getVideoById(videoId);
            if ((!video.duration || !video.thumbnail) && video.source !== 'native') {
              try {
                const url = `${BASE_URL}/videos?part=snippet,contentDetails&id=${videoId}&key=${getNextApiKey()}`;
                const res = await fetch(url);
                const data = await res.json();
                const item = data.items?.[0];
                if (item) {
                  video = {
                    id: videoId,
                    title: item.snippet.title,
                    thumbnail: item.snippet.thumbnails.high?.url || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                    duration: parseISODuration(item.contentDetails.duration),
                    type: 'video',
                  };
                }
              } catch {
                if (video.source !== 'native') {
                  video.thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }
                video.duration = 0;
                video.type = 'video';
              }
            }
            return {
              ...video,
              progress: entry.progress,
              lastPosition: entry.lastPosition,
              totalWatched: entry.totalWatched,
            };
          })
        );

        const nonShortVideos = videosWithDetails.filter(
          (v) => !isShortVideo(v) && v.title !== 'Unknown Title'
        );
        setVideos(nonShortVideos);
      } catch (err) {
        console.error('Failed to load continue watching:', err);
      } finally {
        setLoading(false);
      }
    };

    loadContinueWatching();
  }, [currentUser]);

  if (loading) {
    return (
      <section className="mb-8" aria-labelledby="continue-heading">
        <h2 id="continue-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--vb-accent)' }}>
          {t('sections.continueWatching')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-video rounded-xl animate-pulse"
              style={{ backgroundColor: 'var(--vb-bg-hover)' }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!videos.length) {
    return null;
  }

  return (
    <section className="mb-8" aria-labelledby="continue-heading">
      <div className="flex items-center justify-between mb-4">
        <h2 id="continue-heading" className="text-xl font-bold" style={{ color: 'var(--vb-accent)' }}>
          {t('sections.continueWatching')}
        </h2>
        <button
          onClick={() => setExpanded(prev => !prev)}
          className="text-sm transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded px-2 py-1"
          style={{ color: 'var(--vb-text-muted)' }}
        >
          {expanded ? `${t('sections.showLess')} ←` : `${t('sections.viewAllHistory')} →`}
        </button>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${(expanded ? videos : videos.slice(0, 4)).length > 3 ? 'lg:grid-cols-4' : ''} gap-4`}>
        {(expanded ? videos : videos.slice(0, 4)).map((video) => (
          <VideoCard
            key={video.id}
            id={video.id}
            title={video.title}
            thumbnail={video.thumbnail}
            duration={video.duration}
            aspectRatio={isShortVideo(video) ? 'short' : 'video'}
            href={getVideoRoute(video)}
            showProgress
            progress={Math.round(video.progress * 100)}
            badge={`${Math.round(video.progress * 100)}%`}
            source={video.source}
            youtubeId={video.youtubeId}
            streams={video.streams}
            downloadUrl={video.downloadUrl}
            rawUrl={video.rawUrl}
            channelName={video.channelName}

            creatorAvatar={video.creatorAvatar}
            timeAgo={video.timeAgo}
            viewCount={video.viewCount}
          />
        ))}
      </div>
    </section>
  );
}

export default React.memo(ContinueWatching);
